// SPDX-FileCopyrightText: 2022-2026 Lari Natri <lari.natri@iki.fi>
// SPDX-License-Identifier: GPL-3.0-or-later
import type { PdfjsViewer } from "@unilarva/pdfjs-viewer";
import { Logger } from "../../shared/src/generic/logger";

/** A validated, independently addressable transposition family. Page indices are zero-based. */
export interface MultikeyOccurrence {
  readonly songId: string;
  readonly occurrence: number;
  readonly layerPrefix: string;
  readonly originalKey: string | null;
  readonly keyLabels: readonly (string | null)[] | null;
  readonly pageIndex: number | null;
  readonly anchor: Readonly<{ x: number; y: number }> | null;
  readonly layerPageIndices: readonly number[];
}

const MAX_RAW = 2_000_000;
const MAX_SONGS = 5000;
const PROTOCOL = "ulsbs:transpose:v1";
/** Single parent-app preference entry, independent of compiled PDF filenames. */
export const MULTIKEY_STORAGE_KEY = "unilaiva:multikey:v1";
type Books = Record<string, Record<string, string>>;
const object = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const text = (value: unknown, max = 256): value is string =>
  typeof value === "string" &&
  value.length > 0 &&
  value.length <= max &&
  !/[\u0000-\u001f]/u.test(value);
const dictionary = <T>(): Record<string, T> => Object.create(null) as Record<string, T>;

/** Bounded decoding always creates prototype-free dictionaries. */
function decode(raw: string | null): Books {
  const books = dictionary<Record<string, string>>();
  try {
    if (!raw || raw.length > MAX_RAW) return books;
    const parsed: unknown = JSON.parse(raw);
    if (!object(parsed) || parsed.schema_version !== 1 || !object(parsed.books)) return books;
    for (const [book, songs] of Object.entries(parsed.books).slice(0, 1000)) {
      if (!text(book) || !object(songs)) continue;
      const clean = dictionary<string>();
      for (const [song, key] of Object.entries(songs).slice(0, MAX_SONGS)) {
        if (
          text(song) &&
          text(key, 64) &&
          (!key.startsWith("offset:") || /^offset:(0[0-9]|1[01])$/.test(key))
        )
          clean[song] = key;
      }
      if (Object.keys(clean).length) books[book] = clean;
    }
  } catch {
    /* Corrupt or unavailable persistence is not a reader failure. */
  }
  return books;
}

/** Shared runtime preferences; injectable browser ports keep denial and cross-tab behavior testable. */
export class MultikeyStore {
  private books: Books;
  private readonly pending = new Map<string, Map<string, string | null>>();
  private readonly cleared = new Set<string>();
  private readonly listeners = new Set<(book: string, source?: object) => void>();
  private readonly onStorage = (event: Event): void => {
    const e = event as StorageEvent;
    if (e.key !== MULTIKEY_STORAGE_KEY && e.key !== null) return;
    if (e.storageArea && e.storageArea !== this.storage) return;
    const previous = this.books;
    // Storage events are queued: the event snapshot may predate this tab's own later write.
    this.books = this.read() ?? decode(e.key === null ? null : e.newValue);
    this.applyPending();
    for (const book of new Set([...Object.keys(previous), ...Object.keys(this.books)])) {
      this.notify(book);
    }
  };

  /** Uses memory alone when storage is denied or absent. */
  public constructor(
    private readonly storage?: Pick<Storage, "getItem" | "setItem">,
    private readonly events?: EventTarget,
  ) {
    this.books = this.read() ?? dictionary<Record<string, string>>();
    events?.addEventListener("storage", this.onStorage);
  }

  private read(): Books | undefined {
    try {
      return this.storage ? decode(this.storage.getItem(MULTIKEY_STORAGE_KEY)) : undefined;
    } catch {
      return undefined;
    }
  }

  /** Returns the current runtime preference, including successful unsaved choices. */
  public get(book: string, song: string): string | undefined {
    return this.books[book]?.[song];
  }

  /** Atomically clears a book when requested, then patches songs; denied writes retain both intents. */
  public update(
    book: string,
    changes: ReadonlyMap<string, string | null> | null,
    source?: object,
    clearBook = false,
  ): void {
    // Re-read before writing so another view/tab's unrelated choices are never overwritten.
    // Keep failed writes as patches, not snapshots, so denied storage cannot erase runtime choices.
    if (changes === null || clearBook) {
      this.cleared.add(book);
      this.pending.delete(book);
    }
    if (changes !== null)
      this.pending.set(book, new Map([...(this.pending.get(book) ?? []), ...changes]));
    const latest = this.read();
    if (latest) this.books = latest;
    this.applyPending();
    try {
      const raw = JSON.stringify({ schema_version: 1, books: this.books });
      // Never replace readable disk data with a snapshot our bounded decoder would discard.
      if (
        this.storage &&
        raw.length <= MAX_RAW &&
        Object.keys(this.books).length <= 1000 &&
        Object.values(this.books).every(songs => Object.keys(songs).length <= MAX_SONGS)
      ) {
        this.storage.setItem(MULTIKEY_STORAGE_KEY, raw);
        this.pending.clear();
        this.cleared.clear();
      }
    } catch {
      /* Retain the successful runtime choice despite quota/security errors. */
    }
    this.notify(book, source);
  }

  private notify(book: string, source?: object): void {
    for (const listener of this.listeners) {
      try {
        listener(book, source);
      } catch (error) {
        Logger.debug("[multikey] store listener failed", error);
      }
    }
  }

  private applyPending(): void {
    for (const book of this.cleared) delete this.books[book];
    for (const [id, patch] of this.pending) {
      const songs = this.books[id] ?? dictionary<string>();
      for (const [song, key] of patch) {
        if (key === null) delete songs[song];
        else songs[song] = key;
      }
      if (Object.keys(songs).length) this.books[id] = songs;
      else delete this.books[id];
    }
  }

  /** Subscribes to successful runtime writes and external storage events. */
  public subscribe(listener: (book: string, source?: object) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Releases an explicitly created store's browser listener. */
  public dispose(): void {
    this.events?.removeEventListener("storage", this.onStorage);
    this.listeners.clear();
  }
}

const stores = new WeakMap<object, MultikeyStore>();
function browserStore(root: EventTarget): MultikeyStore {
  const window = (root as HTMLElement).ownerDocument?.defaultView;
  const owner = window ?? root;
  let store = stores.get(owner);
  if (!store) {
    let storage: Storage | undefined;
    try {
      storage = window?.localStorage;
    } catch {
      /* SecurityError: use runtime memory. */
    }
    store = new MultikeyStore(storage, window ?? undefined);
    stores.set(owner, store);
  }
  return store;
}

/** Validates global v2 metadata; optional inspected names additionally validate actual OCG families. */
export function parseMultikeyManifest(
  raw: unknown,
  pageCount: number,
  layerNames?: readonly string[],
): readonly MultikeyOccurrence[] | null {
  if (typeof raw !== "string" || raw.length > MAX_RAW) return null;
  let envelope: unknown;
  try {
    envelope = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!object(envelope) || envelope.has_multikey_info !== true || !object(envelope.multikey_info))
    return null;
  const info = envelope.multikey_info;
  if (
    info.schema_version !== 2 ||
    info.layer_protocol !== PROTOCOL ||
    info.coordinate_space !== "pdf-user-space" ||
    !Array.isArray(info.songs) ||
    info.songs.length > MAX_SONGS ||
    !Number.isSafeInteger(pageCount) ||
    pageCount < 1
  )
    return null;
  const names = new Map<string, number>();
  for (const name of layerNames ?? []) names.set(name, (names.get(name) ?? 0) + 1);
  const prefixes = new Set<string>();
  const identities = new Set<string>();
  const occurrences: MultikeyOccurrence[] = [];
  const page = (value: unknown): value is number =>
    Number.isSafeInteger(value) && (value as number) >= 0 && (value as number) < pageCount;
  for (const song of info.songs) {
    if (
      !object(song) ||
      !text(song.song_id) ||
      !Number.isSafeInteger(song.occurrence) ||
      (song.occurrence as number) < 1 ||
      !text(song.layer_prefix, 600) ||
      !song.layer_prefix.startsWith(`${PROTOCOL}:id:`) ||
      prefixes.has(song.layer_prefix) ||
      !(song.original_key === null || text(song.original_key, 64)) ||
      typeof song.has_transposable_content !== "boolean" ||
      !(song.page_index === null || page(song.page_index)) ||
      !Array.isArray(song.layer_page_indices) ||
      song.layer_page_indices.length > pageCount
    )
      return null;
    const identity = JSON.stringify([song.song_id, song.occurrence]);
    if (identities.has(identity)) return null;
    identities.add(identity);
    prefixes.add(song.layer_prefix);
    const pages = song.layer_page_indices;
    if (
      pages.some((p, i) => !page(p) || (i > 0 && p <= pages[i - 1])) ||
      song.has_transposable_content !== pages.length > 0
    )
      return null;
    const anchor = song.anchor;
    if (!(
      anchor === null ||
      (object(anchor) &&
        (anchor.x === null || (typeof anchor.x === "number" && Number.isFinite(anchor.x))) &&
        (anchor.y === null || (typeof anchor.y === "number" && Number.isFinite(anchor.y))))
    ))
      return null;
    const labels = song.key_labels;
    if (
      labels !== null &&
      (!Array.isArray(labels) ||
        labels.length !== 12 ||
        !labels.every(v => v === null || text(v, 64)) ||
        new Set(labels.filter(v => v !== null)).size !== labels.filter(v => v !== null).length)
    )
      return null;
    if (!song.has_transposable_content) continue;
    const family = Array.from({ length: 12 }, (_, offset) =>
      names.get(`${song.layer_prefix}:t${String(offset).padStart(2, "0")}`),
    );
    if (layerNames !== undefined && family.some(count => count !== 1)) return null;
    occurrences.push(
      Object.freeze({
        songId: song.song_id,
        occurrence: song.occurrence as number,
        layerPrefix: song.layer_prefix,
        originalKey: song.original_key as string | null,
        keyLabels:
          labels === null || labels.every(v => v === null)
            ? null
            : Object.freeze([...labels] as (string | null)[]),
        pageIndex: song.page_index as number | null,
        anchor:
          anchor === null || anchor.x === null || anchor.y === null
            ? null
            : Object.freeze({ x: anchor.x as number, y: anchor.y as number }),
        layerPageIndices: Object.freeze([...pages]),
      }),
    );
  }
  return Object.freeze(occurrences);
}

/** Atomic materialization boundary. Failure leaves the mapping unchanged; success means committed,
 * even if the signal is subsequently aborted. The controller alone owns selected offsets. */
export interface MultikeySelectionBackend {
  /** Applies a complete selection vector against the caller's actual committed snapshot. */
  apply(
    current: ReadonlyMap<MultikeyOccurrence, number>,
    target: ReadonlyMap<MultikeyOccurrence, number>,
    options: { signal: AbortSignal },
  ): Promise<{ ok: true } | { ok: false; reason: "cancelled" | "error"; error?: unknown }>;
  /** Releases backend resources exactly once per controller lifetime. */
  dispose(): void;
}

/** Document-scoped selection inputs; occurrences have already passed manifest validation. */
export interface ControllerOptions {
  viewer: Pick<PdfjsViewer, "documentId">;
  bookId: string;
  root: EventTarget;
  signal: AbortSignal;
  store?: MultikeyStore;
  occurrences: readonly MultikeyOccurrence[];
  backend: MultikeySelectionBackend;
}
type CreationResult =
  | { ok: true; controller: MultikeyController }
  | { ok: false; reason: "cancelled" | "invalid" | "error" };

type Waiter = {
  targets: ReadonlyMap<MultikeyOccurrence, number>;
  finish: (ok: boolean) => void;
};

/** Parent-only selection authority. Desired intent is never published as committed reader state. */
export class MultikeyController {
  public readonly occurrences: readonly MultikeyOccurrence[];
  /** Initial preference restoration, independent of prompt controller creation. */
  public readonly ready: Promise<boolean>;
  private readonly offsets = new Map<MultikeyOccurrence, number>();
  private desired = new Map<MultikeyOccurrence, number>();
  private readonly listeners = new Set<() => void>();
  private readonly waiters = new Set<Waiter>();
  private patches = new Map<string, string | null>();
  private clearBook = false;
  private generation = 0;
  private scheduled = false;
  private running = false;
  private job:
    { abort: AbortController; target: ReadonlyMap<MultikeyOccurrence, number> } | undefined;
  private busy = new Set<MultikeyOccurrence>();
  private disposed = false;
  private unsubscribe: () => void = () => {};
  private readonly abort = (): void => this.dispose();
  private readonly onDocumentChange = (): void => {
    if (!this.active()) this.dispose();
  };
  private constructor(
    private readonly options: ControllerOptions,
    private readonly documentId: number,
    private readonly store: MultikeyStore,
  ) {
    this.occurrences = Object.freeze([...options.occurrences]);
    for (const occurrence of this.occurrences) this.offsets.set(occurrence, 0);
    this.desired = new Map(this.offsets);
    options.root.addEventListener("pdf:statechange", this.onDocumentChange);
    options.signal.addEventListener("abort", this.abort, { once: true });
    this.unsubscribe = store.subscribe((book, source) => {
      if (book === options.bookId && source !== this) void this.restore();
    });
    this.ready = this.restore();
  }

  /** Captures the numeric document identity and starts restoration without awaiting conversion. */
  public static async create(options: ControllerOptions): Promise<CreationResult> {
    let controller: MultikeyController | undefined;
    const id = options.viewer.documentId;
    const stale = (): boolean => options.signal.aborted || options.viewer.documentId !== id;
    try {
      if (stale() || id === null) return { ok: false, reason: "cancelled" };
      if (
        !Number.isSafeInteger(id) ||
        !text(options.bookId) ||
        !Array.isArray(options.occurrences) ||
        new Set(options.occurrences).size !== options.occurrences.length ||
        typeof options.backend?.apply !== "function" ||
        typeof options.backend.dispose !== "function"
      )
        return { ok: false, reason: "invalid" };
      controller = new MultikeyController(options, id, options.store ?? browserStore(options.root));
      return { ok: true, controller };
    } catch (error) {
      controller?.dispose();
      Logger.debug("[multikey] initialization failed", error);
      return { ok: false, reason: stale() ? "cancelled" : "error" };
    }
  }

  private active(): boolean {
    return (
      !this.disposed &&
      !this.options.signal.aborted &&
      this.options.viewer.documentId === this.documentId
    );
  }

  /** Current committed offset. Unknown occurrences are never mutation targets. */
  public offset(occurrence: MultikeyOccurrence): number {
    return this.offsets.get(occurrence) ?? 0;
  }

  private preference(occurrence: MultikeyOccurrence, offset: number): string | null {
    if (offset === 0) return null;
    if (occurrence.keyLabels === null) return `offset:${String(offset).padStart(2, "0")}`;
    const key = occurrence.keyLabels[offset] ?? null;
    if (key === null)
      Logger.debug("[multikey] unlabeled selection is session-only", occurrence.songId, offset);
    return key;
  }

  private resolve(occurrence: MultikeyOccurrence, key: string | undefined): number {
    if (key === undefined) return 0;
    const index = occurrence.keyLabels
      ? occurrence.keyLabels.indexOf(key)
      : /^offset:(0[0-9]|1[01])$/.test(key)
        ? Number(key.slice(7))
        : -1;
    if (index >= 0) return index;
    Logger.debug(
      "[multikey] preference unavailable; using local original",
      occurrence.songId,
      occurrence.occurrence,
      key,
    );
    return 0;
  }

  private notify(): void {
    for (const listener of this.listeners) {
      if (this.disposed) break;
      try {
        listener();
      } catch (error) {
        Logger.debug("[multikey] listener failed", error);
      }
    }
  }

  private matches(
    a: ReadonlyMap<MultikeyOccurrence, number>,
    b: ReadonlyMap<MultikeyOccurrence, number>,
  ): boolean {
    return [...a].every(([o, offset]) => b.get(o) === offset);
  }

  /** Reports page-level work, including unchanged songs sharing an affected page. */
  public pending(occurrence?: MultikeyOccurrence): boolean {
    return occurrence === undefined ? this.busy.size > 0 : this.busy.has(occurrence);
  }

  private updatePending(): void {
    const pages = new Set<number>();
    for (const o of this.occurrences) {
      // An aborted job can already have committed but not yet acknowledged its mapping.
      if (
        this.desired.get(o) !== this.offset(o) ||
        (this.job && this.job.target.get(o) !== this.offset(o))
      )
        for (const page of o.layerPageIndices) pages.add(page);
    }
    const next = new Set(this.occurrences.filter(o => o.layerPageIndices.some(p => pages.has(p))));
    if (next.size !== this.busy.size || [...next].some(o => !this.busy.has(o))) {
      this.busy = next;
      this.notify();
    }
  }

  private request(targets: ReadonlyMap<MultikeyOccurrence, number>): Promise<boolean> {
    const result = new Promise<boolean>(finish => this.waiters.add({ targets, finish }));
    this.generation++;
    this.job?.abort.abort();
    // A request survives coalescing only while all its requested targets survive.
    for (const waiter of this.waiters) {
      if (!this.matches(waiter.targets, this.desired)) {
        this.waiters.delete(waiter);
        waiter.finish(false);
      }
    }
    this.schedule();
    this.updatePending();
    return result;
  }

  private schedule(): void {
    if (this.scheduled || this.running || this.disposed) return;
    this.scheduled = true;
    queueMicrotask(() => {
      this.scheduled = false;
      void this.drain();
    });
  }

  private settle(ok: boolean): void {
    for (const waiter of this.waiters)
      waiter.finish(ok && this.matches(waiter.targets, this.offsets));
    this.waiters.clear();
  }

  private async drain(): Promise<void> {
    if (!this.active()) {
      this.dispose();
      return;
    }
    this.running = true;
    try {
      while (this.active() && this.waiters.size) {
        const generation = this.generation;
        const target = new Map(this.desired);
        const patches = new Map(this.patches);
        const clearBook = this.clearBook;
        let ok = true;
        if (!this.matches(target, this.offsets)) {
          const job = { abort: new AbortController(), target };
          this.job = job;
          try {
            const result = await this.options.backend.apply(new Map(this.offsets), target, {
              signal: job.abort.signal,
            });
            ok = result.ok;
            if (!result.ok && result.reason === "error" && generation === this.generation)
              Logger.debug("[multikey] mutation failed", result.error);
          } catch (error) {
            ok = false;
            if (generation === this.generation) Logger.debug("[multikey] mutation failed", error);
          }
          this.job = undefined;
          if (!this.active()) {
            this.dispose();
            break;
          }
          // Success is authoritative even when cancellation arrived after the physical commit.
          if (ok) {
            for (const [o, offset] of target) this.offsets.set(o, offset);
            this.notify();
          }
        }
        if (!this.active()) {
          this.dispose();
          break;
        }
        if (generation !== this.generation) {
          this.updatePending();
          continue;
        }
        if (!ok) {
          this.desired = new Map(this.offsets);
          this.patches.clear();
          this.clearBook = false;
        }
        this.updatePending();
        // Both committed-state and pending-state listeners may synchronously replace intent.
        if (!this.active()) {
          this.dispose();
          break;
        }
        if (generation !== this.generation) continue;
        this.patches.clear();
        this.clearBook = false;
        if (ok && (clearBook || patches.size)) {
          try {
            this.store.update(this.options.bookId, patches, this, clearBook);
          } catch (error) {
            Logger.debug("[multikey] persistence failed", error);
          }
        }
        if (generation === this.generation && this.active()) this.settle(ok);
      }
    } finally {
      this.running = false;
      if (this.waiters.size) this.schedule();
    }
  }

  /** Retries saved preferences without deleting unavailable keys or persisting restoration. */
  public restore(): Promise<boolean> {
    if (!this.active()) return Promise.resolve(false);
    this.patches.clear();
    this.clearBook = false;
    this.desired = new Map(
      this.occurrences.map(o => [
        o,
        this.resolve(o, this.store.get(this.options.bookId, o.songId)),
      ]),
    );
    return this.request(new Map(this.desired));
  }

  /** Synchronously advances desired intent; repeated families resolve the same absolute key. */
  public change(occurrence: MultikeyOccurrence, direction: -1 | 0 | 1): Promise<boolean> {
    if (!this.active() || !this.offsets.has(occurrence) || ![-1, 0, 1].includes(direction))
      return Promise.resolve(false);
    const offset = direction === 0 ? 0 : (this.desired.get(occurrence)! + direction + 12) % 12;
    return this.select(occurrence, offset);
  }

  /** Selects one absolute family offset atomically, including reset during pending work. */
  public select(occurrence: MultikeyOccurrence, offset: number): Promise<boolean> {
    if (
      !this.active() ||
      !this.offsets.has(occurrence) ||
      !Number.isInteger(offset) ||
      offset < 0 ||
      offset > 11
    )
      return Promise.resolve(false);
    const preference = this.preference(occurrence, offset);
    const targets = new Map(
      this.occurrences
        .filter(o => o.songId === occurrence.songId)
        .map(
          o => [o, o === occurrence ? offset : this.resolve(o, preference ?? undefined)] as const,
        ),
    );
    for (const [o, target] of targets) this.desired.set(o, target);
    this.patches.set(occurrence.songId, preference);
    return this.request(targets);
  }

  /** Restores only known transposition families, then clears this book including absent saved songs. */
  public resetAll(): Promise<boolean> {
    if (!this.active()) return Promise.resolve(false);
    this.desired = new Map(this.occurrences.map(o => [o, 0]));
    this.patches.clear();
    this.clearBook = true;
    return this.request(new Map(this.desired));
  }

  /** Subscribes to committed offsets and page-level pending changes; listener errors are isolated. */
  public subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Idempotently detaches document-scoped listeners and cancels queued work. */
  public dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.job?.abort.abort();
    this.options.root.removeEventListener("pdf:statechange", this.onDocumentChange);
    this.options.signal.removeEventListener("abort", this.abort);
    this.unsubscribe();
    this.settle(false);
    this.busy.clear();
    this.listeners.clear();
    try {
      this.options.backend.dispose();
    } catch (error) {
      Logger.debug("[multikey] backend disposal failed", error);
    }
  }
}
